// client/src/api/leadsApi.js
//
// F6 M2 - Cliente API del panel comercial de leads.
//
// Wrapper de los 4 endpoints admin que expone server (requireAdmin):
//   GET    /admin/leads
//   GET    /admin/leads/:id
//   PATCH  /admin/leads/:id
//   POST   /admin/leads/:id/notes
//
// Patron alineado con dashboardAPI.js (clase + funciones export).

import { ENV } from "../Utils";

async function doFetch(url, params = {}) {
  const res = await fetch(url, params);
  const ct = res.headers.get("content-type") || "";
  const parse = ct.includes("application/json")
    ? () => res.json()
    : () => res.text();
  const data = await parse().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.message || `Request error ${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

const authHeaders = (token) =>
  token ? { Authorization: `Bearer ${token}` } : {};

function buildQueryString(params = {}) {
  const cleaned = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  );
  if (cleaned.length === 0) return "";
  return `?${cleaned
    .map(
      ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
    )
    .join("&")}`;
}

export class LeadsApi {
  /**
   * Listar leads paginado + filtros.
   * @param {string} accessToken
   * @param {object} filters - { page, pageSize, vertical, status, assigned_to,
   *                              search, from, to }
   */
  list(accessToken, filters = {}) {
    const qs = buildQueryString(filters);
    return doFetch(`${ENV.BASE_API}/admin/leads${qs}`, {
      method: "GET",
      headers: { Accept: "application/json", ...authHeaders(accessToken) },
    });
  }

  get(accessToken, id) {
    return doFetch(`${ENV.BASE_API}/admin/leads/${id}`, {
      method: "GET",
      headers: { Accept: "application/json", ...authHeaders(accessToken) },
    });
  }

  /**
   * Drip M3.1 - Correos de nurture enviados a este lead + estado del drip.
   * @returns {Promise<{data:{sequence, steps:[], emails:[], drip:{}}}>}
   */
  listLeadEmails(accessToken, id) {
    return doFetch(`${ENV.BASE_API}/admin/leads/${id}/emails`, {
      method: "GET",
      headers: { Accept: "application/json", ...authHeaders(accessToken) },
    });
  }

  /**
   * Drip M3.2 - Pausar / reanudar el drip de un lead.
   * @returns {Promise<{data:{drip:{}}}>}
   */
  setDripPaused(accessToken, id, paused) {
    return doFetch(`${ENV.BASE_API}/admin/leads/${id}/drip`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders(accessToken),
      },
      body: JSON.stringify({ paused }),
    });
  }

  /**
   * Drip M5 - Activar/retirar el consentimiento de marketing desde el admin.
   * El backend rechaza con 409 si el usuario se dio de baja explicita.
   * @returns {Promise<{data:{drip:{}}}>}
   */
  setDripConsent(accessToken, id, consent) {
    return doFetch(`${ENV.BASE_API}/admin/leads/${id}/drip`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders(accessToken),
      },
      body: JSON.stringify({ consent }),
    });
  }

  /**
   * Drip M3.2 - "Enviar ahora": manda un paso del drip al lead (manual).
   */
  sendDripNow(accessToken, id, step) {
    return doFetch(`${ENV.BASE_API}/admin/leads/${id}/drip/send`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders(accessToken),
      },
      body: JSON.stringify({ step }),
    });
  }

  /**
   * @param {object} patch - { status?, assigned_to? } (null para desasignar)
   */
  patch(accessToken, id, patch) {
    return doFetch(`${ENV.BASE_API}/admin/leads/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders(accessToken),
      },
      body: JSON.stringify(patch),
    });
  }

  addNote(accessToken, id, text) {
    return doFetch(`${ENV.BASE_API}/admin/leads/${id}/notes`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders(accessToken),
      },
      body: JSON.stringify({ text }),
    });
  }

  /**
   * F6 M5 - Users a los que se puede asignar un lead (admins por ahora).
   */
  listAssignableUsers(accessToken) {
    return doFetch(`${ENV.BASE_API}/admin/leads-assignable-users`, {
      method: "GET",
      headers: { Accept: "application/json", ...authHeaders(accessToken) },
    });
  }
}

export const leadsApi = new LeadsApi();
